import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import CommonGrid from "../../../../Repository/CommonGrid";
import { useTranslation } from "react-i18next";
import { cloneDeep, debounce } from "lodash";
import LightTooltip from "../../../../../commonComponents/LightTooltip";
import { styles } from "./ServiceDetailsTab";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import {
  fieldTypeOptionsArray,
  TRANSLATE_OUTBOUND_CONDITION_MAPPING_2,
} from "../../../../../constants/constantList";
import FormattingOptions from "../../../Forms/FormattingOptions";
import { ConstantValueControl } from "../../../Forms/OutboundFieldFormattingOptions/ConstantValueControl";
import LabeledCheckbox from "../../../../TemplateProperties/TPCheckbox";
import { removeLowerCaseAndUndefined } from "../../../../../utils/utilFunctions";
import WorkflowSourceType from "../../../../../commonComponents/WorkflowSourceTypeComponent/WorkflowSourceType";
import FormattingOptionsCheckboxes from "../../../Forms/FormattingOptionsCheckboxes";

const InValuesTab = ({ submitServiceDetails, screen }) => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState(null);
  const [currentInValues, setCurrentInValues] = useState([]);
  const [selectedArgName, setSelectedArgName] = useState("");
  const [selectedFieldType, setSelectedFieldType] = useState("ALPHA");
  const currentSelectedRow = useRef({});
  const [currentSelectedRowState, setCurrentSelectedRowState] = useState({});
  const parentScreen = useContext(ParentScreenCtx);
  const selectedNode = useSelector(
    (state) => state.treeData[parentScreen].rightTree.tree.body.selectedNode
  );
  
  // Dynamic reference for INBOUND/OUTBOUND based on screen type
  const nodeData = selectedNode?.[screen === 'GET_DATA' ? 'INBOUND' : 'OUTBOUND'];
  const constantValueDropDownMenu = ["", t("CRLF"), t("RECORD_DELIMITER")];
  const [constantValue, setConstantValue] = useState("");
  const [repeatTimes, setRepeatTimes] = useState("1");
  const [ignoreGlobalSubstitution, setIgnoreGlobalSubstitution] =
    useState(false);
  const [bitPosition, setBitPosition] = useState("1");
  const [bitValue, setBitValue] = useState("0");
  const [selectedRecordName, setSelectedRecordName] = useState("SELECT");
  const [dontEscapeXML, setDontEscapeXML] = useState(false);

  const fieldOptionsDropdownArray = cloneDeep(fieldTypeOptionsArray);
  fieldOptionsDropdownArray.splice(3, null, {
    value: "CONSTANT",
    text: "CONSTANT",
  });
  screen === "SEND_DATA" && fieldOptionsDropdownArray?.push({ value: "TRANSLATED_FORWARDED_DATA", text: t("TRANSLATED_FORWARDED_DATA") })

  const fieldTypeDropDownItems = fieldOptionsDropdownArray.map((obj, key) => (
    <MenuItem key={key} value={obj.value}>
      {t(obj.text)}
    </MenuItem>
  ));

  const recordNameOptions = TRANSLATE_OUTBOUND_CONDITION_MAPPING_2.slice(
    0,
    5
  ).map((obj, key) => (
    <MenuItem key={key} value={obj.value}>
      {t(obj.title)}
    </MenuItem>
  ));

  const bitValueDropdown = useMemo(
    () =>
      ["0", "1"].map((e, i) => (
        <MenuItem key={`bitValue-${i}`} value={e}>
          {e}
        </MenuItem>
      )),
    []
  );

  const bitPositionDropdown = useMemo(
    () =>
      ["1", "2", "3", "4", "5", "6", "7", "8"].map((e, i) => (
        <MenuItem key={`bitPosition-${i}`} value={e}>
          {e}
        </MenuItem>
      )),
    []
  );

  const updateGridOnly = useCallback((data = [], selectionIndex, gridAPI) => {
    if (!gridAPI) {
      setCurrentInValues([]);
      return;
    }

    const rowData = data.map((item, index) => ({
      ...item,
      id: index,
    }));

    gridAPI.setGridOption("rowData", rowData);
    setCurrentInValues(rowData);

    setTimeout(() => {
      gridAPI.forEachNode((node) => {
        if (node?.data?.id === selectionIndex) {
          node.setSelected(true);
          gridAPI.ensureNodeVisible(node);
        }
      });
    }, 10);
  }, []);


  const deleteActionHandler = (info) => {
    const data = [];
    info.api.forEachNode((node) => {
      if (info.data.id !== node.data.id) data.push(node.data);
    });
    debouncedUpdateGridData([...data], 0, info.api);
  };

  const CustomCellRenderer = (info) => {
    const { value } = info;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "2rem",
        }}
      >
        <div className="truncated-text" title={value}>
          {value}
        </div>
        <div className="flex align-center">
          <LightTooltip onClick={() => deleteActionHandler(info)}>
            <span style={{ cursor: "pointer" }} className="DELETE_MUI"></span>
          </LightTooltip>
        </div>
      </div>
    );
  };

  const customClickHandlerOnAdd = (e) => {
    const colIdArr = [];
    e.api.forEachNode((node) => colIdArr.push(node.data.id));
    const newColId = colIdArr.length > 0 ? Math.max(...colIdArr) + 1 : 0;

    const newRow = {
      ARGNAME: "UNNAMED",
      ARGTYPE: "IN",
      FIELDTYPE: "ALPHA",
      id: newColId,
    };

    const data = [];
    const selectedRows = e.api.getSelectedRows();
    e.api.forEachNode((node) => {
      data.push(node.data);
    });

    data.splice(+(selectedRows?.[0]?.id || 0) + 1, 0, newRow);
    debouncedUpdateGridData([...data], newColId, e.api);
  };

  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t("MNU_WS_NEW_IN_VALUE"),
        headerName: "",
        field: "ARGNAME",
        customClickHandlerOnAdd: customClickHandlerOnAdd,
        cellRenderer: (info) => CustomCellRenderer(info),
        editable: false,
        resizable: false,
        flex: 1,
      },
    ],
    suppressMovableColumns: true,
  };

  function handleDataUpdate(key, value, bulk) {
    const data = cloneDeep(currentInValues);
    if (!bulk) {
      if (value) data[currentSelectedRow.current.id][key] = value;
      else
        data[currentSelectedRow.current.id].hasOwnProperty(key) &&
          delete data[currentSelectedRow.current.id][key];
    } else {
      Object.keys(bulk).forEach((e) => {
        if (bulk[e]) data[currentSelectedRow.current.id][e] = bulk[e];
        else if(bulk[e] === null) data[currentSelectedRow.current.id]?.hasOwnProperty(e) && delete data[currentSelectedRow.current.id][e];
      });
    }

    currentSelectedRow.current = data[currentSelectedRow.current.id];
    setCurrentSelectedRowState(currentSelectedRow.current);
    debouncedUpdateGridData(data, currentSelectedRow.current.id, gridApi);
  }

  const debouncedUpdateGridData = useCallback(
    debounce((data = [], selectionIndex, gridAPI) => {
      
      updateGridOnly(data, selectionIndex, gridAPI);
      if (gridAPI) {
        submitServiceDetails({
          add: {
            ARGS: [
              ...(nodeData?.ARGS?.filter(
                (item) => item.ARGTYPE === "OUT"
              ) || []),
              ...(data.map((obj) => removeLowerCaseAndUndefined(obj))),
            ],
          },
        });
      }
    }, 300),
    [updateGridOnly, submitServiceDetails, nodeData]
  );

  const handleFieldTypeChange = (e) => {
    const newFieldType = e.target.value;
    setSelectedFieldType(newFieldType);

    // Update the current row data
    if (currentSelectedRow.current?.id !== undefined) {
      const updatedData = currentInValues.map((row) => {
        const { RECORD_TYPE, WF_ITEM, WF_ITEM_ID, OUTBOUND, MAPNAME, WF_ITEM_DEPENDENCY_TIMEOUT, ...returnObj} = cloneDeep(row);
        const removeFields = ["PRECISION", "PRECISION_OUT", "ROUND", "VALUE"];

        if (row.id === currentSelectedRow.current.id) {
          if (newFieldType === "BIT_FLAG") {
            let BitFields = {
              BIT_POSITION: "1",
              LENGTH: "0",
              PRECISION: "0",
              PRECISION_OUT: "0",
              ROUND: "TRUE",
            };

            return {
              ...returnObj,
              ...BitFields,
              FIELDTYPE: newFieldType,
            };
          } else if (newFieldType === "FLOAT") {
            // Remove other fields but keep ROUND for FLOAT
            const fieldsToRemove = removeFields.filter(field => field !== "ROUND");
            fieldsToRemove.forEach((item) => {
              returnObj.hasOwnProperty(item) && delete returnObj[item];
            });

            return {
              ...returnObj,
              ROUND: "TRUE",
              FIELDTYPE: newFieldType,
            };
          } else {
            removeFields.forEach((item) => {
              returnObj.hasOwnProperty(item) && delete returnObj[item];
            });

            return {
              ...returnObj,
              FIELDTYPE: newFieldType,
            };
          }
        }
        return row;
      });
      debouncedUpdateGridData(updatedData, currentSelectedRow.current.id, gridApi);
    }
  };

  const handleRow = (options) => {
    const selectedRow = options.api.getSelectedRows()[0];

    if (selectedRow?.id !== undefined) {
      currentSelectedRow.current = { ...selectedRow };
      setCurrentSelectedRowState(currentSelectedRow.current);
      setSelectedArgName(selectedRow?.ARGNAME || "");
      setSelectedFieldType(selectedRow?.FIELDTYPE || "ALPHA");
      options.api.redrawRows();
    }
  };

  useEffect(() => {
    if (gridApi && nodeData?.ARGS?.length > 0) {
      const inArgs = nodeData.ARGS.filter(
        (item) => item.ARGTYPE === "IN"
      ).map((arg) => ({
        ...arg,
        FIELDTYPE: arg.FIELDTYPE || "ALPHA", // Add default field type if not present
      }));
      updateGridOnly(inArgs, currentSelectedRow?.current?.id || 0, gridApi);
    }
  }, [selectedNode?.key, gridApi, updateGridOnly]);

  useEffect(() => {
    if (currentSelectedRowState) {
      setSelectedRecordName(currentSelectedRowState?.RECORD_TYPE || "SELECT");
      setConstantValue(currentSelectedRowState?.VALUE || "");
      setRepeatTimes(currentSelectedRowState?.REPEAT || "1");
      setBitPosition(currentSelectedRowState?.BIT_POSITION || "1");
      setBitValue(currentSelectedRowState?.VALUE || "0");
      setIgnoreGlobalSubstitution(
        currentSelectedRowState?.IGNORE_GLOBAL_SUB === "TRUE" ? true : false
      );
      setDontEscapeXML(
        currentSelectedRowState?.DONT_ESCAPE_XML === "TRUE" ? true : false
      );
      setSelectedFieldType(currentSelectedRowState?.FIELDTYPE || "ALPHA");
      setSelectedArgName(currentSelectedRowState?.ARGNAME || "");
    }
  }, [currentSelectedRowState]);

  return (
    <div style={styles.serviceContainer}>
      <div className="width-100" style={{ height: "220px" }}>
        <CommonGrid
          gridOptions={gridOptions}
          rowData={currentInValues}
          getGridAPI={(e, gridApi) => {
            setGridApi(gridApi.api);
          }}
          containerStyle={{ height: "100% !important" }}
          gridResizable={true}
          gridProps={{
            rowSelection: "single",
            onSelectionChanged: handleRow,
          }}
        />
      </div>

      {
        currentInValues?.length > 0 &&
        <>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            className="width-100"
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <TextField
                label={t("LBL_NAME")}
                variant="outlined"
                className="width-100"
                size="small"
                value={selectedArgName}
                disabled={nodeData?.USE_WSDL === "TRUE" ? true : false}
                name="ARGNAME"
                onChange={(e) => {
                  setSelectedArgName(e.target.value);
                  handleDataUpdate("ARGNAME", e.target.value);
                }}
              />

              <FormControl variant="outlined" className="width-100" size="small">
                <InputLabel id="ftDropdownLabel">{t("LBL_FLD_TYPE")}</InputLabel>
                <Select
                  className="width-100"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                  name="FIELDTYPE"
                  value={selectedFieldType}
                  labelId="ftDropdownLabel"
                  label={t("LBL_FLD_TYPE")}
                  id="ftDropdown"
                  size="small"
                  onChange={handleFieldTypeChange}
                  disabled={currentInValues?.length === 0}
                >
                  {fieldTypeDropDownItems}
                </Select>
              </FormControl>

              {currentInValues?.length !== 0 && selectedFieldType !== "CONSTANT" && selectedFieldType !== "TRANSLATED_FORWARDED_DATA" && (
                <>
                  <FormControl
                    variant="outlined"
                    className="width-100"
                    size="small"
                  >
                    <InputLabel id="sourceRecordName">
                      {t("LBL_SOURCE_RECORD_NAME")}
                    </InputLabel>
                    <Select
                      className="width-100"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      name="RECORD_TYPE"
                      value={selectedRecordName}
                      labelId="sourceRecordNameLabel"
                      label={t("LBL_SOURCE_RECORD_NAME")}
                      id="sourceRecordName"
                      size="small"
                      onChange={(e) => {
                        setSelectedRecordName(e.target.value);
                        handleDataUpdate(null, null, {
                          [e?.target?.name]: e?.target?.value,
                          MAPNAME: null,
                          WF_ITEM_ID: null,
                          WF_ITEM: null,
                          OUTBOUND_NAME: null,
                          WF_ITEM_DEPENDENCY_TIMEOUT: null,
                        });
                      }}
                    >
                      {recordNameOptions}
                    </Select>
                  </FormControl>

                  {currentSelectedRowState?.RECORD_TYPE && (
                    <WorkflowSourceType
                      node={{
                        key: currentSelectedRowState?.id,
                        RECORD_TYPE: currentSelectedRowState?.RECORD_TYPE,
                        WFITEM: currentSelectedRowState?.WF_ITEM,
                        OUTBOUND_NAME: currentSelectedRowState?.OUTBOUND,
                        MAPNAME: currentSelectedRowState?.MAPNAME,
                        WF_ITEM_DEPENDENCY_TIMEOUT:
                          currentSelectedRowState?.WF_ITEM_DEPENDENCY_TIMEOUT,
                      }}
                      valuesHandler={(values) => {
                        handleDataUpdate(null, null, {
                          RECORD_TYPE: values?.RECORD_TYPE,
                          WF_ITEM: values?.WFITEM,
                          OUTBOUND: values?.OUTBOUND_NAME,
                          MAPNAME: values?.MAPNAME,
                          WF_ITEM_ID: values?.WF_ITEM_ID,
                        });
                      }}
                      onlySessionData={true}
                      obLabel={t("LBL_OUT_TEM")}
                    />
                  )}
                </>
              )}
            </div>

            {selectedFieldType === "CONSTANT" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  marginTop: "15px",
                }}
              >
                <ConstantValueControl
                  constantValue={constantValue}
                  setConstantValue={(value) => {
                    setConstantValue(value);
                    if (
                      currentSelectedRowState?.REPEAT &&
                      currentSelectedRowState?.LENGTH
                    ) {
                      //change length based on constant value * repeat times
                      const newLength =
                        parseInt(repeatTimes) * (value?.length || 0);
                      handleDataUpdate(null, null, {
                        VALUE: value,
                        LENGTH: newLength.toString(),
                      });
                    } else {
                      handleDataUpdate("VALUE", value);
                    }
                  }}
                  constantValueOptions={constantValueDropDownMenu}
                />

                <FormControl variant="outlined" className="width-100" size="small">
                  <TextField
                    autoComplete={"off"}
                    size="small"
                    value={repeatTimes}
                    label={t("LBL_REPEAT_#_TIMES")}
                    onBlur={(e) => {
                      if (e.target.value === "" || e.target.value === "0") {
                        setRepeatTimes(currentSelectedRowState?.REPEAT || "1");
                      }
                    }}
                    onChange={(e) => {
                      const value = typeof e?.target?.value === "string" ? e.target.value : "";
                      const newValue = value.substring(0, 9).replaceAll(/\D/g, "");

                      if (newValue === "" || newValue === "0") {
                        setRepeatTimes(newValue);
                        return;
                      }

                      if (newValue !== repeatTimes) {
                        const newLength = parseInt(newValue) * constantValue.length;
                        setRepeatTimes(newValue);
                        handleDataUpdate(null, null, {
                          REPEAT: newValue,
                          LENGTH: newLength.toString(),
                        });
                      }
                    }}
                  />
                </FormControl>

                <FormattingOptionsCheckboxes
                  node={currentSelectedRowState}
                  valuesHandler={handleDataUpdate}
                  hideControls={["URLENCODE", "RPT_TRUNC_ERR", "IGNORE_GLOBAL_SUB", "ROUND"]}
                  showQuoteData={true}
                  callerComponent="IN_VALUES"
                />
              </div>
            )}

            {selectedFieldType === "TRANSLATED_FORWARDED_DATA" && (
              <div>
                <LabeledCheckbox
                  label={t("BTN_WS_DONT_ESCAPE_XML")}
                  checked={dontEscapeXML}
                  handleCheck={(checked) => {
                    setDontEscapeXML(checked);
                    handleDataUpdate("DONT_ESCAPE_XML", checked ? "TRUE" : null);
                  }}
                />
              </div>
            )}

            {Object.keys(currentSelectedRowState || {}).length > 0 && <FormattingOptions
              node={currentSelectedRowState}
              valueshandler={handleDataUpdate}
              hideControls={["URLENCODE", "RPT_TRUNC_ERR", "IGNORE_GLOBAL_SUB"]}
              showQuoteData={true}
              callerComponent="IN_VALUES"
              hideInboundPattern={true}
            />}
          </div>
        </>
      }
    </div>
  );
};

export default InValuesTab;
